/**
 * @lines98/client/lib/in-progress-cache — кэш НЕЗАВЕРШЁННОЙ не-daily партии
 * (all-time / casual) в браузере для сквозного сохранения (run-persistence-lifecycle.md
 * T-B / addendum-001 Решение 2).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-06-13): раньше незавершённая партия
 *   кэшировалась ТОЛЬКО для доски дня (`services/daily-cache.ts`, гейт
 *   `kind!=='daily'`); в остальных режимах закрытие вкладки/навигация молча теряли
 *   прогресс. T-B требует кэшировать ВСЕ режимы и по «Играть» возвращать игрока в
 *   открытую партию (Pro играет днями). Этот модуль — ОБОБЩЁННЫЙ in-progress-кэш для
 *   не-daily (all_time + casual): один источник правды (НЕ копия по экранам — CLAUDE.md
 *   «код через модули»; code-structure §1/§3). Daily остаётся в своём `daily-cache.ts`
 *   (у него своя машинерия played-снимка + rollover — не трогаем, §11).
 *
 * КОНТРАКТ (addendum Решение 2 «что кэшировать»):
 *   - all_time: `sessionId + seed + mode + moves[] + engineVersion (+ startedAt)`.
 *     Это daily-кэш ПЛЮС `sessionId` (привязка к серверной сессии для finish-game).
 *     `seed` кэшируется ЯВНО (для all-time нет даты, из которой его восстановить).
 *   - casual: `seed + mode + moves[] + engineVersion (+ startedAt)` — без серверной
 *     сессии (возобновление чисто локальное, сервер не участвует).
 *   `startedAt` (клиентское) — только UX/диагностика/выбор «самой недавней» партии
 *   при resume (P1-1), НЕ авторитетно (серверное время — правда; addendum §6.8).
 *
 * SCOPE: персист одной активной не-daily партии (save после каждого хода, load для
 *   resume, clear на finish). Один разделённый по `kind` record. IDB + localStorage-
 *   fallback (как `services/daily-cache.ts` / `services/identity.ts` — тот же паттерн).
 * NOT IN SCOPE: daily (свой модуль); серверная сессия/heartbeat (RP-5); resume-поток
 *   (RP-4, `lib/run-resume`); зачёт/submit (game-submit).
 *
 * Cross-references:
 *   - run-persistence-lifecycle.md §5 (T-B) + addendum-001 Решение 2.
 *   - services/daily-cache.ts (sibling — daily in-progress + IDB/localStorage паттерн).
 *   - store/game-store.ts (GameContext all_time/casual — источник полей).
 *   - lib/run-resume.ts (RP-4 — читает этот кэш на входе «Играть»).
 */

import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';

import type { GameMode, Move } from '@lines98/shared/types/game';

// =============================================================================
// Types — discriminated «active run» record
// =============================================================================

/** Common fields of any non-daily in-progress run. */
interface RunBase {
  /** Schema version — bump on shape change (mismatched record → discarded on read). */
  readonly v: 1;
  /** uint32 seed of the run. For all_time this is the SERVER seed (NOT re-derivable). */
  readonly seed: number;
  /** Game mode (next-visibility). */
  readonly mode: GameMode;
  /** Recorded valid moves (replay restores the board from seed+moves). */
  readonly moves: Move[];
  /** Engine version the run was played with (ADR-0004 — replay on the same version). */
  readonly engineVersion: string;
  /**
   * Client wall-clock at run start (ms epoch). UX/diagnostics + «most recent run»
   * tie-break at resume (P1-1). NOT authoritative — server time is truth (§6.8).
   */
  readonly startedAt: number;
}

/** All-time scored run — bound to a server `game_sessions` session_id. */
export interface AllTimeRunRecord extends RunBase {
  readonly kind: 'all_time';
  /** Server session id (`POST /game/start`) — finish-game verifies it. */
  readonly sessionId: string;
}

/** Casual (non-scored) run — purely local, no server session. */
export interface CasualRunRecord extends RunBase {
  readonly kind: 'casual';
}

/** The single active non-daily in-progress run (all_time | casual). */
export type InProgressRun = AllTimeRunRecord | CasualRunRecord;

// =============================================================================
// Constants
// =============================================================================

/** Single IDB/localStorage key for the active non-daily run (one run at a time). */
const RUN_KEY = 'lines98.run.in_progress.v1';

// =============================================================================
// IDB / localStorage helpers (mirrors daily-cache.ts sibling pattern)
// =============================================================================

async function readKey<T>(key: string, guard: (v: unknown) => v is T): Promise<T | null> {
  try {
    const raw = (await idbGet(key)) as unknown;
    if (guard(raw)) return raw;
    return null;
  } catch {
    try {
      const fallback = typeof window !== 'undefined' ? window.localStorage?.getItem(key) : null;
      if (!fallback) return null;
      const parsed = JSON.parse(fallback) as unknown;
      return guard(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}

async function writeKey(key: string, value: unknown): Promise<void> {
  try {
    await idbSet(key, value);
  } catch {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage?.setItem(key, JSON.stringify(value));
      }
    } catch {
      // both failed — non-fatal; in-memory state continues correctly.
    }
  }
}

async function deleteKey(key: string): Promise<void> {
  try {
    await idbDel(key);
  } catch {
    // ignore
  }
  try {
    if (typeof window !== 'undefined') {
      window.localStorage?.removeItem(key);
    }
  } catch {
    // ignore
  }
}

// =============================================================================
// Type guards
// =============================================================================

function isMove(value: unknown): value is Move {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['sr'] === 'number' &&
    typeof v['sc'] === 'number' &&
    typeof v['r'] === 'number' &&
    typeof v['c'] === 'number'
  );
}

function isMode(value: unknown): value is GameMode {
  return value === 'predict' || value === 'classic' || value === 'blind';
}

function hasRunBase(v: Record<string, unknown>): boolean {
  return (
    v['v'] === 1 &&
    typeof v['seed'] === 'number' &&
    isMode(v['mode']) &&
    Array.isArray(v['moves']) &&
    (v['moves'] as unknown[]).every(isMove) &&
    typeof v['engineVersion'] === 'string' &&
    typeof v['startedAt'] === 'number'
  );
}

function isInProgressRun(value: unknown): value is InProgressRun {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (!hasRunBase(v)) return false;
  if (v['kind'] === 'all_time') return typeof v['sessionId'] === 'string' && v['sessionId'].length > 0;
  if (v['kind'] === 'casual') return true;
  return false;
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Save the current active non-daily run. Overwrites any prior run (one active run at
 * a time — the player is in exactly one game). Call after every accepted move.
 *
 * Defensive: serialization failure degrades UX gracefully (server is the truth for
 * all_time scoring), never blocks play. `moves` is copied (caller may keep mutating).
 */
export async function saveRun(run: InProgressRun): Promise<void> {
  const moves = run.moves.slice();
  const persisted: InProgressRun =
    run.kind === 'all_time'
      ? {
          v: 1,
          kind: 'all_time',
          sessionId: run.sessionId,
          seed: run.seed >>> 0,
          mode: run.mode,
          moves,
          engineVersion: run.engineVersion,
          startedAt: run.startedAt,
        }
      : {
          v: 1,
          kind: 'casual',
          seed: run.seed >>> 0,
          mode: run.mode,
          moves,
          engineVersion: run.engineVersion,
          startedAt: run.startedAt,
        };
  await writeKey(RUN_KEY, persisted);
}

/**
 * Load the active non-daily run, OR `null` if absent / corrupted. A record with a
 * mismatched schema version / shape is treated as absent (the guard rejects it).
 */
export async function loadRun(): Promise<InProgressRun | null> {
  return readKey(RUN_KEY, isInProgressRun);
}

/** Clear the active non-daily run (call on finish / explicit reset). */
export async function clearRun(): Promise<void> {
  await deleteKey(RUN_KEY);
}

/** Test seam — clear the run record. Tests only. */
export async function _clearRunForTests(): Promise<void> {
  await deleteKey(RUN_KEY);
}
